/**
 * Visitor detail — one visit's full record: photo (tap to enlarge), who they
 * were, why they came, gate timeline (expected / arrived / in / out), ID and
 * vehicle. Wired: GET /api/visitors/:id. MyGate-parity screen; the design
 * bundle has no source for this — composed in the app's own idiom.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Image, Linking, Pressable, View } from 'react-native';

import { space, type PillStatus } from '@bero/tokens';
import { str } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { useT, monthShort } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Card, EmptyState, Pill, Screen, Sheet, Skeleton, TopBar } from '@/ui';

type Visit = {
  name: string; type: string; status: PillStatus; companions: number;
  phone?: string; company?: string; orderRef?: string; vehicleNo?: string;
  photo?: string; idType?: string; notes?: string; approvedByName?: string;
  expectedAt?: string; expectedUntil?: string; arrivedAt?: string;
  checkedInAt?: string; checkedOutAt?: string; createdAt?: string;
};

const STATUSES: PillStatus[] = ['open', 'progress', 'urgent', 'resolved', 'approved', 'pending', 'rejected'];
const TYPE_KEY: Record<string, DictKey> = {
  VISITOR: 'vd_type_guest', DELIVERY: 'vd_type_delivery', CAB: 'vd_type_cab', STAFF: 'vd_type_staff',
};

function toVisit(v: unknown): Visit {
  const rawStatus = (str(v, 'status') ?? 'pending').toLowerCase();
  return {
    name: str(v, 'name') ?? 'Visitor',
    type: str(v, 'type') ?? 'VISITOR',
    status: (STATUSES as string[]).includes(rawStatus) ? (rawStatus as PillStatus) : 'pending',
    companions: Number(str(v, 'companions') ?? 0) || 0,
    phone: str(v, 'phone'),
    company: str(v, 'company'),
    orderRef: str(v, 'orderRef'),
    vehicleNo: str(v, 'vehicleNo'),
    photo: str(v, 'photo'),
    idType: str(v, 'idType'),
    notes: str(v, 'notes'),
    approvedByName: str(v, 'approvedByName'),
    expectedAt: str(v, 'expectedAt'),
    expectedUntil: str(v, 'expectedUntil'),
    arrivedAt: str(v, 'arrivedAt'),
    checkedInAt: str(v, 'checkedInAt'),
    checkedOutAt: str(v, 'checkedOutAt'),
    createdAt: str(v, 'createdAt'),
  };
}

/** Large arch photo/avatar hero. Photo taps open the full-screen view. */
function Hero({ visit, onExpand }: { visit: Visit; onExpand: () => void }) {
  const { colors } = useTheme();
  const { t } = useT();
  const W = 128;
  const shape = {
    width: W, height: W * 1.15,
    borderTopLeftRadius: W / 2, borderTopRightRadius: W / 2,
    borderBottomLeftRadius: W * 0.11, borderBottomRightRadius: W * 0.11,
    overflow: 'hidden' as const,
  };
  if (visit.photo) {
    return (
      <Pressable onPress={onExpand} accessibilityRole="imagebutton" accessibilityLabel={t('vd_view_photo')}>
        <Image source={{ uri: visit.photo }} style={[shape, { backgroundColor: colors.canvas2 }]} resizeMode="cover" />
        <View style={{
          position: 'absolute', right: 6, bottom: 6, width: 28, height: 28, borderRadius: 14,
          backgroundColor: colors.canvas0, alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="plus" size={14} color={colors.textSecondary} />
        </View>
      </Pressable>
    );
  }
  const initials = visit.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <View style={[shape, { backgroundColor: colors.primary100, alignItems: 'center', justifyContent: 'center' }]}>
      <AppText weight="700" style={{ color: colors.primary900, fontSize: W * 0.28, lineHeight: W * 0.36 }}>{initials}</AppText>
      <AppText variant="bodySm" style={{ color: colors.primary900, opacity: 0.7, marginTop: 4 }}>{t('vd_no_photo')}</AppText>
    </View>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: space.s4, paddingVertical: 6 }}>
      <AppText variant="bodySm" tone="tertiary">{label}</AppText>
      <AppText variant="bodyMd" weight="500" style={{ flex: 1, textAlign: 'right' }}>{value}</AppText>
    </View>
  );
}

/** "7 Jul · 6:00 PM" — a gate log needs the clock, not just the day. */
function fmtStamp(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
  return `${d.getDate()} ${monthShort(d.getMonth())} · ${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
}

/** Gate timeline row: dot + label + time. */
function TimeRow({ label, iso, last }: { label: string; iso?: string; last?: boolean }) {
  const { colors } = useTheme();
  if (!iso) return null;
  return (
    <View style={{ flexDirection: 'row', gap: space.s3 }}>
      <View style={{ alignItems: 'center', width: 12 }}>
        <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary500, marginTop: 6 }} />
        {!last ? <View style={{ flex: 1, width: 2, backgroundColor: colors.borderSoft, marginTop: 2 }} /> : null}
      </View>
      <View style={{ paddingBottom: last ? 0 : space.s3, flex: 1, flexDirection: 'row', justifyContent: 'space-between' }}>
        <AppText variant="bodyMd" tone="secondary">{label}</AppText>
        <AppText variant="bodyMd" weight="500">{fmtStamp(iso)}</AppText>
      </View>
    </View>
  );
}

export default function VisitorDetail() {
  const { colors } = useTheme();
  const { t } = useT();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [visit, setVisit] = React.useState<Visit | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [photoOpen, setPhotoOpen] = React.useState(false);

  React.useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await api.get<unknown>(`/api/visitors/${encodeURIComponent(id)}`);
        const raw = (body && typeof body === 'object' && 'visitor' in body)
          ? (body as { visitor: unknown }).visitor : body;
        if (!cancelled) { setVisit(toVisit(raw)); setState('ready'); }
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const purpose = visit
    ? [TYPE_KEY[visit.type] ? t(TYPE_KEY[visit.type]) : visit.type, visit.company].filter(Boolean).join(' · ')
    : '';

  return (
    <Screen scroll={false}>
      <TopBar title={t('vd_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3, alignItems: 'center', marginTop: space.s4 }}>
            <Skeleton w={128} h={147} />
            <Skeleton w={160} h={20} />
            <Skeleton h={120} />
          </View>
        ) : state === 'error' || !visit ? (
          <EmptyState
            icon={<Icon name="users" size={40} color={colors.textTertiary} />}
            title={t('vd_err_title')}
            body={t('vd_err_body')}
            cta={t('vd_back')}
            onCta={() => router.back()}
          />
        ) : (
          <>
            <View style={{ alignItems: 'center', marginTop: space.s2 }}>
              <Hero visit={visit} onExpand={() => setPhotoOpen(true)} />
              <AppText variant="headingMd" weight="600" style={{ marginTop: space.s4, textAlign: 'center' }}>
                {visit.name}{visit.companions > 0 ? ` +${visit.companions}` : ''}
              </AppText>
              {purpose ? (
                <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 2 }}>{purpose}</AppText>
              ) : null}
              {visit.orderRef ? (
                <AppText mono variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{visit.orderRef}</AppText>
              ) : null}
              <View style={{ marginTop: space.s3 }}>
                <Pill status={visit.status} />
              </View>
              {visit.phone ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('vd_call_a11y', { name: visit.name })}
                  onPress={() => void Linking.openURL(`tel:${visit.phone}`)}
                  style={{
                    marginTop: space.s3, flexDirection: 'row', alignItems: 'center', gap: 6,
                    paddingHorizontal: space.s4, paddingVertical: 8, borderRadius: 999,
                    borderWidth: 1, borderColor: colors.borderFirm,
                  }}
                >
                  <Icon name="phone" size={14} color={colors.textPrimary} />
                  <AppText variant="bodySm" weight="600">{t('vd_call')}</AppText>
                </Pressable>
              ) : null}
            </View>

            <Card style={{ marginTop: space.s5 }}>
              <TimeRow label={t('vd_expected_from')} iso={visit.expectedAt} />
              <TimeRow label={t('vd_expected_until')} iso={visit.expectedUntil} />
              <TimeRow label={t('vd_arrived')} iso={visit.arrivedAt} />
              <TimeRow label={t('vd_checked_in')} iso={visit.checkedInAt} />
              <TimeRow label={t('vd_checked_out')} iso={visit.checkedOutAt} last />
              {!visit.expectedAt && !visit.arrivedAt && !visit.checkedInAt ? (
                <AppText variant="bodySm" tone="tertiary">{t('vd_no_timings')}</AppText>
              ) : null}
            </Card>

            <Card style={{ marginTop: space.s3 }}>
              <DetailRow label={t('pinfo_phone')} value={visit.phone} />
              <DetailRow label={t('vd_company')} value={visit.company} />
              <DetailRow label={t('vd_vehicle')} value={visit.vehicleNo} />
              <DetailRow label={t('vd_id_checked')} value={visit.idType} />
              <DetailRow label={t('vd_approved_by')} value={visit.approvedByName} />
              {!visit.phone && !visit.company && !visit.vehicleNo && !visit.idType && !visit.approvedByName ? (
                <AppText variant="bodySm" tone="tertiary">{t('vd_no_details')}</AppText>
              ) : null}
            </Card>

            {visit.notes ? (
              <Card style={{ marginTop: space.s3 }}>
                <AppText variant="label" tone="tertiary">{t('vd_notes')}</AppText>
                <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>{visit.notes}</AppText>
              </Card>
            ) : null}
          </>
        )}
      </Screen>

      {/* Full-screen photo view */}
      <Sheet visible={photoOpen} onClose={() => setPhotoOpen(false)}>
        {visit?.photo ? (
          <Image
            source={{ uri: visit.photo }}
            style={{ width: '100%', aspectRatio: 3 / 4, borderRadius: 16, backgroundColor: colors.canvas2 }}
            resizeMode="contain"
          />
        ) : null}
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s3, textAlign: 'center' }}>
          {t('vd_captured')}
        </AppText>
      </Sheet>
    </Screen>
  );
}
